'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useIsMobile } from '@/hooks/useIsMobile';

import type { FlatPackagesProps } from '../../types';
import CategoryPackages from './CategoryPackages';

export { useIsMobile } from '@/hooks/useIsMobile';

export default function Packages({
  data = [],
  labels,
  onEnquire = (): void => {},
  onBook,
}: FlatPackagesProps): React.ReactElement {
  const isMobile = useIsMobile();
  const totalCategories = data.length;
  const didScrollRef = useRef(false);

  const [userTouchedCats, setUserTouchedCats] = useState(false);

  const visibleCount = userTouchedCats
    ? totalCategories
    : Math.min(isMobile ? 2 : 3, totalCategories);

  useEffect((): void => {
    if (!window.location.hash) return;
    setUserTouchedCats(true);
  }, []);

  const visibleList = useMemo(
    () => data.slice(0, visibleCount),
    [data, visibleCount],
  );

  // Runs again whenever `visibleList` grows (e.g. once the hash above expands
  // it to include every category), so it can find a target category that
  // wasn't in the DOM yet on the first pass.
  useEffect((): void => {
    if (didScrollRef.current) return;
    const hash = decodeURIComponent(window.location.hash.slice(1));
    if (!hash) return;
    const element = document.getElementById(hash);
    if (!element) return;
    didScrollRef.current = true;
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [visibleList]);

  const handleLoadMoreCategories = (): void => setUserTouchedCats(true);

  const basePkgCount = isMobile ? 2 : 3;

  return (
    <section className="no-scroll-reveal content-wrapper font-regular mx-auto px-0 py-8">
      {visibleList.map((category, ci) => (
        <CategoryPackages
          key={category?.slug || `cat-${ci}`}
          category={category}
          ci={ci}
          isMobile={isMobile}
          basePkgCount={basePkgCount}
          labels={labels}
          isLast={ci === visibleList.length - 1}
          onEnquire={onEnquire}
          onBook={onBook}
        />
      ))}

      {totalCategories > visibleCount && (
        <div className="mt-12 flex justify-center">
          <button
            onClick={handleLoadMoreCategories}
            className="border-theme-primary hover:text-theme-primary bg-theme-primary flex cursor-pointer items-center gap-2 rounded-sm border px-5 py-2 text-base font-bold text-white hover:bg-white"
          >
            Load More
          </button>
        </div>
      )}
    </section>
  );
}
